import clsx from 'clsx';

import { LightningBorder } from '../LightningBorder';

import styles from './Section.module.css';

type Props = {
  children: React.ReactNode;
  className?: string;
  Component?: React.ComponentType | keyof JSX.IntrinsicElements;
};

const SectionBackground = ({ children, className, Component = 'section' }: Props) => (
  // @ts-ignore TODO error TS2741: Property ''case-id'' is missing in type
  <Component className={className}>{children}</Component>
);

const SectionContent = ({ children, className, Component = 'div' }: Props) => (
  // @ts-ignore TODO error TS2741: Property ''case-id'' is missing in type
  <Component className={clsx(styles.content, className)}>{children}</Component>
);

const Section = ({ children, className, Component = 'section' }: Props) => (
  <SectionBackground className={className} Component={Component}>
    <SectionContent>{children}</SectionContent>
  </SectionBackground>
);

Section.Background = SectionBackground;
Section.Content = SectionContent;

const withVerticalSpacing =
  <T extends { className?: string }>(Component: React.ComponentType<T>) =>
  ({ className, ...restOfProps }: T) => {
    const props = { className: clsx(styles.verticalSpacing, className), ...restOfProps } as T;
    return <Component {...props} />;
  };

const SectionWithVerticalSpacing = withVerticalSpacing(Section);

const swapSectionColor = (color: SectionColor) => (color === 'light' ? 'dark' : 'light');

type SectionPosition = 'top' | 'bottom';
type SectionColor = 'light' | 'dark';

type InterlacingProps = {
  startPosition?: SectionPosition;
  startColor?: SectionColor;
  invertColor?: boolean;
  withLightningBorder?: boolean;
  isHeader?: boolean;
};

const InterlacingSectionBackground = ({
  children,
  className,
  Component,
  startPosition = 'top',
  startColor = 'light',
  invertColor = false,
  withLightningBorder = false,
  isHeader = false,
}: Props & InterlacingProps) => (
  <SectionBackground
    className={clsx(
      styles.interlacingBackground,
      className,
      styles[startPosition],
      styles[invertColor ? swapSectionColor(startColor) : startColor],
      isHeader && styles.header,
    )}
    Component={Component}
  >
    {children}
    {withLightningBorder && <InterlacingSectionLightningBorder />}
  </SectionBackground>
);

const InterlacingSectionContent = ({ children, className, Component }: Props) => (
  <SectionContent className={clsx(styles.interlacingContent, className)} Component={Component}>
    {children}
  </SectionContent>
);

const InterlacingSectionBox = ({ children, className, Component = 'div' }: Props) => (
  // @ts-ignore TODO error TS2741: Property ''case-id'' is missing in type
  <Component className={clsx(styles.interlacingBox, className)}>{children}</Component>
);

const InterlacingSectionLightningBorder = () => <LightningBorder className={styles.interlacingLightningBorder} />;

const InterlacingSection = ({
  children,
  contentClassName,
  ...props
}: Props & InterlacingProps & { contentClassName?: string }) => (
  <InterlacingSectionBackground {...props}>
    <InterlacingSectionContent className={contentClassName}>{children}</InterlacingSectionContent>
  </InterlacingSectionBackground>
);

InterlacingSection.Background = InterlacingSectionBackground;
InterlacingSection.Content = InterlacingSectionContent;
InterlacingSection.Box = InterlacingSectionBox;
InterlacingSection.LightningBorder = InterlacingSectionLightningBorder;

// Designed to be used as a spacer between two sections in content wise but bundled in one interlacing section for the sake of background colors
const Spacer = ({ className }: { className?: string }) => <div className={clsx(styles.spacer, className)} />;

const DoubleColumnsSection = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <SectionContent className={clsx(styles.doubleColumnsSection, className)}>{children}</SectionContent>
);

export { Section, SectionWithVerticalSpacing, InterlacingSection, Spacer, withVerticalSpacing, DoubleColumnsSection };
export type { SectionPosition, SectionColor };
